import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { isNullOrUndefined } from '@uzurv/core';
// eslint-disable-next-line import/order
import { UZURV_REST_CONFIGURATION, UzurvRestConfiguration, UzurvRestService } from '@uzurv/http';
import { Deserialize, Serialize } from 'cerialize';
import { cloneDeep, findIndex } from 'lodash';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { OperationsAlert, OperationsAlertInfo, OperationsAlertList, User } from '../../models';

@Injectable({
  providedIn: 'root'
})
export class OperationsAlertsService extends UzurvRestService {
  readonly resourceUrl = 'operationsAlerts/';

  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    protected domSanitizer: DomSanitizer
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  // Utils

  static addOrUpdateOperationsAlertInfoInList(
    operationsAlert: OperationsAlertInfo,
    list: Array<OperationsAlertInfo>
  ): Array<OperationsAlertInfo> {
    if (isNullOrUndefined(list) || isNullOrUndefined(operationsAlert)) {
      return list;
    }

    const clonedList: Array<OperationsAlertInfo> = cloneDeep(list);
    const existingIndex = findIndex(list, oa => oa.id === operationsAlert.id);

    if (existingIndex === -1) {
      clonedList.push(operationsAlert);
    } else {
      clonedList.splice(existingIndex, 1, operationsAlert);
    }

    return clonedList;
  }

  static removeOperationsAlertInfoFromList(
    operationsAlert: OperationsAlertInfo,
    list: Array<OperationsAlertInfo>
  ): Array<OperationsAlertInfo> {
    if (isNullOrUndefined(list) || isNullOrUndefined(operationsAlert)) {
      return list;
    }

    const clonedList: Array<OperationsAlertInfo> = cloneDeep(list);
    const existingIndex = findIndex(list, oa => oa.id === operationsAlert.id);

    if (existingIndex !== -1) {
      clonedList.splice(existingIndex, 1);
    }

    return clonedList;
  }

  /**
   * This method is overloaded in the backend
   *
   * - When `isAssigned` is not provided the backend will return
   * a list of all nonresolved alerts (assigned or unassigned),
   * and requires the permission `operations_alerts_management` ("All Alerts")
   *
   * - When `isAssigned` is provided, a value of false will return
   * all nonassigned nonresolved alerts ("New Alerts")
   *
   * - When `isAssigned` is provided, a value of true will return
   * all nonresolved alerts assigned to the current user's ID ("My Alerts")
   *
   * @param pageNumber
   * @param pageSize
   * @param isAssigned
   * @permission operations_alerts_management
   * @permission operations_alerts_read
   */

  listAllNonresolved(
    pageNumber: number,
    pageSize: number,
    transitProgramIds?: Array<number>,
    isAssigned?: boolean
  ): Observable<OperationsAlertList> {
    return this.get<OperationsAlertList>(this.resourceUrl, { pageNumber, pageSize, transitProgramIds, isAssigned }).pipe(
      map(response => Deserialize(response, OperationsAlertList))
    );
  }

  /**
   * @param startDate
   * @param endDate
   * @param pageNumber
   * @param pageSize
   * @permission operations_alerts_management
   */
  listAllResolvedByDateRange(
    isResolved: boolean,
    startDate: string,
    endDate: string,
    pageNumber: number,
    pageSize: number
  ): Observable<OperationsAlertList> {
    return this.get<OperationsAlertList>(this.resourceUrl, {
      isResolved: isResolved,
      pageNumber: pageNumber,
      pageSize: pageSize,
      startDate: startDate,
      endDate: endDate
    }).pipe(map(response => Deserialize(response, OperationsAlertList)));
  }

  /**
   * @param objectName
   * @param objectId
   * @param pageNumber
   * @param pageSize
   * @permission operations_alerts_management
   */
  listAllResolvedForObject(objectName: string, objectId: number, pageNumber: number, pageSize: number): Observable<OperationsAlertList> {
    return this.get<OperationsAlertList>(this.resourceUrl, {
      pageNumber: pageNumber,
      pageSize: pageSize,
      objectName: objectName,
      objectId: objectId
    }).pipe(map(response => Deserialize(response, OperationsAlertList)));
  }

  /**
   * @param id
   * @param operationsAlert
   * @permission operations_alerts_update
   */
  patchOperationsAlert(id: number, operationsAlert: OperationsAlert): Observable<OperationsAlert> {
    return this.patch(`${this.resourceUrl}${id}`, Serialize(operationsAlert)).pipe(map(response => Deserialize(response, OperationsAlert)));
  }

  /**
   *
   * @param id
   * @param operationsAlert
   */
  assign(id: number, operationsAlert: OperationsAlert): Observable<OperationsAlert> {
    return this.put<OperationsAlert>(`${this.resourceUrl}${id}/assignee`, operationsAlert).pipe(
      map(response => Deserialize(response, OperationsAlert))
    );
  }

  /**
   *
   * @param id
   */
  unassign(id: number): Observable<OperationsAlert> {
    return this.delete<OperationsAlert>(`${this.resourceUrl}${id}/assignee`).pipe(map(response => Deserialize(response, OperationsAlert)));
  }

  unassignAllByUserId(userId: number): Observable<Array<OperationsAlert>> {
    return this.delete<Array<OperationsAlert>>(`${this.resourceUrl}assignee/${userId}`).pipe(
      map(response => Deserialize(response, OperationsAlert))
    );
  }
}
